import { DefaultValues } from 'react-hook-form'
import { array, boolean, InferType, lazy, mixed, object, string } from 'yup'

import { getAdditionalDriverSchema } from './additionalDriverSchema'
import { addressSchema } from './addressSchema'
import { requiredMessage } from 'directFlow/state/config/content'
import { dateOfBirthSchema } from 'directFlow/state/config/dateOfBirth/schema'

import { EmploymentStatus, MedicalCondition } from 'types'
import { validateEmail } from 'utils'

export const driversPageDefaultValues = {
  title: undefined,
  firstName: undefined,
  lastName: undefined,
  email: undefined,
  emailSubscribe: true,
  dateOfBirth: {
    day: null,
    month: null,
    year: null,
  },
  drivingLicenceNumber: undefined,
  skipDrivingLicenceNumberQuestion: undefined,
  yearsInUk: undefined,
  address: undefined,
  homeowner: undefined,
  student: undefined,
  university: undefined,
  course: undefined,
  universityEmail: undefined,
  drivenDuringTerm: undefined,
  exitSurvey: undefined,
  employment: undefined,
  industry: undefined,
  additionalDrivers: {
    checked: undefined,
    list: [],
  },
  employmentStatus: undefined,
  hasSecondaryEmployment: undefined,
  secondaryEmployment: undefined,
  secondaryIndustry: undefined,
  hasMedicalCondition: undefined,
  medicalCondition: undefined,
} satisfies DefaultValues<DriversSchemaType>

const employedStatuses: EmploymentStatus[] = [
  EmploymentStatus.EMPLOYED,
  EmploymentStatus.SELF_EMPLOYED,
]

export const driversSchema = object({
  title: string().required(requiredMessage),
  firstName: string().trim().required(requiredMessage),
  lastName: string().trim().required(requiredMessage),
  email: string()
    .test('is-valid-email', (value, context) => {
      const { isValid, message } = validateEmail(value)
      return isValid || context.createError({ message: message ?? '' })
    })
    .required(requiredMessage),
  emailSubscribe: boolean().optional(),
  drivingLicenceNumber: string().nullable().optional(), // TODO: Remove once users are no longer using outdated Drivers page state
  skipDrivingLicenceNumberQuestion: boolean().optional(), // TODO: Remove once users are no longer using outdated Drivers page state
  dateOfBirth: dateOfBirthSchema,
  yearsInUk: string().required(requiredMessage),
  address: addressSchema.required(requiredMessage),
  homeowner: boolean().required(requiredMessage),
  student: boolean().required(requiredMessage),
  university: string().when('student', {
    is: false,
    then: () => string().optional(),
    otherwise: () => string().required(requiredMessage),
  }),
  course: string().when('student', {
    is: false,
    then: () => string().optional(),
    otherwise: () => string().required(requiredMessage),
  }),
  universityEmail: string()
    .test('is-valid-email', (value, context) => {
      const { isValid, message } = validateEmail(value)
      return isValid || context.createError({ message: message ?? '' })
    })
    .optional(),
  drivenDuringTerm: boolean()
    .when('student', {
      is: false,
      then: () => boolean().nullable(),
      otherwise: () => boolean().required(requiredMessage),
    })
    .nullable()
    .default(null),
  exitSurvey: string().required(requiredMessage),
  employmentStatus: mixed<EmploymentStatus>()
    .oneOf(Object.values(EmploymentStatus))
    .when('student', {
      is: false,
      then: (schema) => schema.required(requiredMessage),
      otherwise: (schema) => schema.optional(),
    }),
  employment: string().when('employmentStatus', {
    is: (status: EmploymentStatus) => employedStatuses.includes(status),
    then: () => string().required(requiredMessage),
    otherwise: () => string().optional(),
  }),
  industry: string().when('employmentStatus', {
    is: (status: EmploymentStatus) => employedStatuses.includes(status),
    then: () => string().required(requiredMessage),
    otherwise: () => string().optional(),
  }),
  hasSecondaryEmployment: boolean()
    .when('employmentStatus', {
      is: (status: EmploymentStatus) => employedStatuses.includes(status),
      then: () => boolean().nullable().required(requiredMessage),
      otherwise: () => boolean().nullable(),
    })
    .nullable()
    .default(null),
  secondaryEmployment: string().when('hasSecondaryEmployment', {
    is: true,
    then: () => string().required(requiredMessage),
    otherwise: () => string().optional(),
  }),
  secondaryIndustry: string().when('hasSecondaryEmployment', {
    is: true,
    then: () => string().required(requiredMessage),
    otherwise: () => string().optional(),
  }),
  hasMedicalCondition: boolean().nullable().required(requiredMessage),
  medicalCondition: mixed<MedicalCondition>()
    .oneOf(Object.values(MedicalCondition))
    .when('hasMedicalCondition', {
      is: true,
      then: (schema) => schema.required(requiredMessage),
      otherwise: (schema) => schema.optional(),
    }),
  additionalDrivers: object({
    checked: boolean().nullable().required(requiredMessage),
    list: array()
      .of(lazy(() => getAdditionalDriverSchema({})))
      .when('checked', {
        is: true,
        then: (schema) => schema.min(1, 'Please add at least one item or select "No"'),
        otherwise: (schema) => schema.optional().default([]),
      })
      .optional()
      .default([]),
  }),
})

export type DriversSchemaType = InferType<typeof driversSchema>
